import { Link } from 'react-router-dom';
import type { CampanhaCompleta, DissipacaoDaCampanha } from '../../api/cptu';
import { numero, plural } from '../../formato';
import { ErroDaApi, Vazio } from '../../ui';
import { origemDaReta, resumoDoT50, useDissipacoesNaTela, useResumosGravados } from './DissipacaoInterpretada';

/**
 * A aba Dissipações da campanha CPTu — onda 312, Tarefa 312.5; o t₅₀ e «Abrir» da onda 313 (Tarefa 313.5, R11).
 *
 * As dissipações ativas das verticais ativas, na ordem da vertical e da profundidade: o t₅₀ de cada uma com as
 * escolhas gravadas — sempre «pela reta», e «extrapolado» quando a curva medida não chega a u₅₀ (o resultado é do
 * navegador, R2: a tela calcula com o motor do `web`) —, quantos pares (tempo × u), quanto durou, de onde veio a
 * reta (a do projeto do CPeT-IT, gravada com ele, a automática do motor, ou a gravada pela tela) e de que arquivo.
 * «Abrir» leva à dissipação aberta (`/cptu/:id/dissipacoes/:did`), com a curva, as alças e o resultado inteiro.
 */
export function CampanhaDissipacoes({ c }: { c: CampanhaCompleta }) {
  const { itens, erro } = useDissipacoesNaTela(c);
  const resumos = useResumosGravados(itens);
  if (c.dissipacoes.length === 0) {
    return (
      <section className="cartao">
        <Vazio>
          Nenhuma dissipação nesta campanha. A dissipação entra pela importação, junto da vertical
          (a planilha «-DS» do Pagani ou o projeto do CPeT-IT).
        </Vazio>
      </section>
    );
  }
  return (
    <section className="cartao cptu-dissipacoes-da-campanha">
      {erro ? <ErroDaApi erro={erro} /> : null}
      <div className="rola">
        <table aria-label="Dissipações da campanha">
          <thead>
            <tr>
              <th scope="col">Vertical</th>
              <th scope="col" className="n">Profundidade</th>
              <th scope="col">t₅₀</th>
              <th scope="col" className="n col-opcional">Pares</th>
              <th scope="col" className="n col-opcional">Duração</th>
              <th scope="col">Reta</th>
              <th scope="col" className="col-opcional">Origem</th>
              <th scope="col"><span className="so-leitor">Abrir</span></th>
            </tr>
          </thead>
          <tbody>
            {c.dissipacoes.map((d) => {
              const s = resumos.get(d.id);
              return (
                <tr key={d.id}>
                  <th scope="row"><span className="mono">{d.vertical}</span></th>
                  <td className="n mono">{numero(d.profundidade, 2)} m</td>
                  <td className={s?.t50Extrapolado ? 'cptu-t50-da-lista extrapolado' : 'cptu-t50-da-lista'}>
                    {s ? resumoDoT50(s).replace(/^t₅₀ /, '') : itens ? '—' : 'calculando…'}
                  </td>
                  <td className="n mono col-opcional">{numero(d.pares, 0)}</td>
                  <td className="n mono col-opcional">{duracao(d)}</td>
                  <td>{origemDaReta(d)}</td>
                  <td className="col-opcional">
                    {ORIGEM[d.origem] ?? d.origem}
                    {d.arquivo && <span className="sub mono">{d.arquivo}</span>}
                  </td>
                  <td>
                    <Link className="cptu-abrir" to={`/cptu/${c.campanha.id}/dissipacoes/${d.id}`}
                      aria-label={`Abrir a dissipação de ${numero(d.profundidade, 2)} m do ${d.vertical}`}>
                      Abrir
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="cptu-nota-da-tabela">
        {plural(c.dissipacoes.length, 'dissipação ativa', 'dissipações ativas')} · na ordem da vertical e da profundidade
        · o t₅₀ é sempre pela reta, com as escolhas gravadas
      </p>
    </section>
  );
}

const ORIGEM: { [origem: string]: string } = {
  pagani_xlsx: 'Pagani (.xlsx)', cpeit_cpt: 'Projeto do CPeT-IT (.cpt)', csv: 'CSV/TXT',
};

/** «1.500 s (25 min)»: o segundo do equipamento, e o minuto para quem lê. */
function duracao(d: DissipacaoDaCampanha): string {
  const s = d.duracao_s === null || d.duracao_s === undefined ? NaN : Number(d.duracao_s);
  if (!Number.isFinite(s)) return '—';
  const minutos = s / 60;
  return minutos >= 1 ? `${numero(s, 0)} s (${numero(minutos, minutos < 10 ? 1 : 0, true)} min)` : `${numero(s, 0)} s`;
}
